import { ChangeDetectionStrategy, Component, DestroyRef, inject, NgZone, OnInit, ChangeDetectorRef} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { SidebarService } from './core/services/sidebar.service';
import { ThemeService } from './core/services/theme.service';
import { SidebarComponent } from './_shared/components/sidebar/sidebar.component';
import { LoaderComponent } from './_shared/components/loader/loader.component';
import { stringConstant } from './core/services/stringConstants';
import { filter, tap} from 'rxjs';
import { SharedService } from './core/services/shared.service';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from './core/services/authentication.service';
import { SubscriptionService } from './core/services/subscription.service';
import { SubscriptionPlan } from './core/models/subscription.model';
import { environment } from 'src/environments/environment';
import { TokenHandlerService } from './core/services/token-handler.service';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import config from '../../capacitor.config';
import { LoaderService } from './core/services/loader.service';
import { LayoutChromeService } from './core/services/layout-chrome.service';

interface HeaderNavItem {
  label: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonicModule,
    RouterOutlet,
    SidebarComponent,
    LoaderComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly _cdr = inject(ChangeDetectorRef);
  readonly layoutChrome = inject(LayoutChromeService);
  public readonly constant: typeof stringConstant;
  private readonly destroyRef = inject(DestroyRef);
  private hasCheckedSilentAuth = false;

  currentHeaderItem: HeaderNavItem = {
    label: 'Home',
    icon: 'search',
    route: '',
  };

  private readonly headerNavItems: HeaderNavItem[] = [
    { label: 'Home', icon: 'search', route: '' },
    { label: 'Research Book', icon: 'book', route: '/research-book' },
    { label: 'Data Explorer', icon: 'explore', route: '/data-explorer' },
    { label: 'Case Management', icon: 'law', route: '/pm/cases' },
    { label: 'Document Management', icon: 'folder', route: '/dms' },
    { label: 'Firm Workspace', icon: 'groups', route: '/firm' },
    { label: 'Profile', icon: 'profile', route: '/profile' },
    { label: 'Subscription', icon: 'subscription', route: '/subscription' },
  ];

  private readonly materialIconMap: Record<string, string> = {
    explore: 'explore',
    folder: 'folder',
    groups: 'groups',
    profile: 'person',
    subscription: 'card_membership',
  };

  // Get horizontal logo based on current theme
  get horizontalLogoSrc(): string {
    return this.themeService.isDarkTheme()
      ? this.constant.bharatLawHorizontalDark
      : this.constant.bharatLawHorizontal;
  }

  // Signal-based sidebar pinned state — no manual subscription needed
  readonly isSidebarPinned = toSignal(
    inject(SidebarService).sidebarPinned$,
    { initialValue: false },
  );

  /**
   * Constructor
   */
  constructor(
    private sidebarService: SidebarService,
    private themeService: ThemeService,
    private sharedService: SharedService,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    private subscriptionService: SubscriptionService,
    private authService: AuthService,
    private router: Router,
    private tokenHandlerService: TokenHandlerService,
    private ngZone: NgZone,
    private loaderService: LoaderService,
  ) {
    this.constant = stringConstant;

    // Show/hide main loader based on Auth0 loading state
    this.authService.isLoading$.pipe(takeUntilDestroyed()).pipe(tap(() => this._cdr.markForCheck())).subscribe((res) => {
      if (res) {
        this.loaderService.mainLoaderOn();
      } else {
        this.loaderService.mainLoaderOff();
      }
    });
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   *
   * Auth0 Startup Logic for devv2.bharatlaw.ai:
   * - On first load, check if user has Auth0 session cookie
   * - If session exists, silently get token (no login prompt)
   * - If no session, user can click login button manually
   * - Works seamlessly when switching from dev.bharatlaw.ai -> devv2.bharatlaw.ai
   */
  ngOnInit() {
    this.updateMobileHeaderItem();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .pipe(tap(() => this._cdr.markForCheck())).subscribe((event) => {
        this.updateMobileHeaderItem();
        this.trackPreviousJudgementPageUrl(event.urlAfterRedirects);
      });

    // Initialize theme service to apply saved theme
    this.themeService.setTheme(this.themeService.currentTheme);

    // Initialize safe area insets for Android/iOS
    if (Capacitor.isNativePlatform()) {
      SafeArea.getSafeAreaInsets()
        .then((data) => {
          const { insets } = data;
          document.body.style.setProperty(
            this.constant.ionSafeAreaTop,
            `${insets.top}px`,
          );
          document.body.style.setProperty(
            this.constant.ionSafeAreaRight,
            `${insets.right}px`,
          );
          document.body.style.setProperty(
            this.constant.ionSafeAreaBottom,
            `${insets.bottom}px`,
          );
          document.body.style.setProperty(
            this.constant.ionSafeAreaLeft,
            `${insets.left}px`,
          );
        })
        .catch(() => {
          // Handle error silently - fallback to CSS env() variables if plugin fails
        });

      // Listen for safe area changes (e.g., on rotation or keyboard show/hide)
      SafeArea.addListener('safeAreaChanged', (data) => {
        const { insets } = data;
        document.body.style.setProperty(
          this.constant.ionSafeAreaTop,
          `${insets.top}px`,
        );
        document.body.style.setProperty(
          this.constant.ionSafeAreaRight,
          `${insets.right}px`,
        );
        document.body.style.setProperty(
          this.constant.ionSafeAreaBottom,
          `${insets.bottom}px`,
        );
        document.body.style.setProperty(
          this.constant.ionSafeAreaLeft,
          `${insets.left}px`,
        );
      });
    }

    // Register native listener cleanup via DestroyRef (replaces ngOnDestroy)
    if (Capacitor.isNativePlatform()) {
      this.destroyRef.onDestroy(() => {
        App.removeAllListeners();
        SafeArea.removeAllListeners();
      });
    }

    // Handle Auth0 callback for Android/iOS only (web uses existing /callback route)
    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', ({ url }) => {
        // Must run inside an NgZone for Angular to pick up the changes
        // https://capacitorjs.com/docs/guides/angular
        this.ngZone.run(() => {
          // More flexible URL matching - check if URL contains the callback path
          // This handles variations in URL format and ensures we catch the callback
          const callbackPath = `${this.constant.capacitorCallbackPath}/${config.appId}${this.constant.slashCallback}`;
          const callbackUri = `${config.appId}://${environment.auth.domain}${this.constant.capacitorCallbackPath}/${config.appId}${this.constant.slashCallback}`;
          const isCallbackUrl =
            url?.includes(callbackPath) || url?.startsWith(callbackUri);

          if (isCallbackUrl) {
            // If the URL is an authentication callback URL..
            const hasState = url.includes('state=');
            const hasError = url.includes('error=');
            const hasCode = url.includes('code=');

            if (hasState && (hasError || hasCode)) {
              // Close the browser first
              Browser.close().catch(() => {});

              // Parse the deep link URL to extract query parameters
              // Custom URL scheme format: com.bharatlaw.app://domain/path?code=...&state=...
              const queryStartIndex = url.indexOf('?');
              if (queryStartIndex !== -1) {
                const queryString = url.substring(queryStartIndex + 1);
                const params = new URLSearchParams(queryString);

                // Convert URLSearchParams to object for router navigation
                const queryParams: { [key: string]: string } = {};
                params.forEach((value, key) => {
                  queryParams[key] = value;
                });

                // Navigate to the callback route with the query parameters
                // The existing GetTokenComponent will handle the token exchange
                // This avoids CORS issues because the SDK handles it internally
                this.router.navigate([this.constant.slashCallback], {
                  queryParams,
                });
              } else {
                // If no query params, just navigate to callback
                this.router.navigate([this.constant.slashCallback]);
              }
            } else {
              // If URL doesn't have required params, just close browser
              Browser.close().catch(() => {});
            }
          }
        });
      });
    }

    // Check if user is authenticated and has a user role (existing logic)
    const existingToken = this.sharedService.getLocalStorage(
      this.constant.token,
    );
    if (
      existingToken &&
      this.sharedService.getLocalStorage(this.constant.userRole) ===
        this.constant.user
    ) {
      this.setPlanInLocalStorage();
    } else if (!existingToken) {
      // No token in localStorage - try silent authentication
      // This handles first-time navigation to devv2.bharatlaw.ai
      // and seamless switch from dev.bharatlaw.ai -> devv2.bharatlaw.ai
      this.checkSilentAuthentication();
    }
  }

  /**
   * Toggles the sidebar
   */
  toggleSidebar() {
    this.sidebarService.toggleSidebar();
  }

  /**
   * Returns Material Symbol name for supported icons.
   */
  getMaterialIconName(icon: string): string | null {
    return this.materialIconMap[icon] ?? null;
  }

  /**
   * Remembers the last non-judgement page URL so the judgement view's
   * back button can return to it (with its original query params/filters)
   * instead of falling back to the default search page.
   */
  private trackPreviousJudgementPageUrl(url: string): void {
    if (url.startsWith(this.constant.viewJudgementRoute)) {
      return;
    }
    this.sharedService.setLocalStorage(
      this.constant.previousJudgementPageUrlKey,
      url,
    );
  }

  /**
   * Updates mobile header icon/title based on active route.
   */
  private updateMobileHeaderItem(): void {
    const currentRoute = this.router.url.split('?')[0];

    const matched =
      this.headerNavItems.find(
        (item) =>
          item.route === '' && (currentRoute === '/' || currentRoute === ''),
      ) ||
      this.headerNavItems.find(
        (item) =>
          item.route.startsWith('/pm/') && currentRoute.startsWith('/pm/'),
      ) ||
      this.headerNavItems.find(
        (item) =>
          item.route.startsWith('/dms') && currentRoute.startsWith('/dms'),
      ) ||
      this.headerNavItems.find(
        (item) =>
          item.route !== '' &&
          (currentRoute === item.route ||
            currentRoute.startsWith(item.route + '/')),
      );

    this.currentHeaderItem = matched ?? this.headerNavItems[0];
  }

  /**
   * Check for silent authentication on app startup
   * Attempts to get token silently if Auth0 session exists
   * This enables seamless login when user switches from dev.bharatlaw.ai to devv2.bharatlaw.ai
   */
  private checkSilentAuthentication(): void {
    // Prevent multiple attempts
    if (this.hasCheckedSilentAuth) {
      return;
    }

    // Don't interfere with callback route (let GetTokenComponent handle it)
    const currentUrl = this.router.url;
    if (currentUrl.includes(this.constant.slashCallback)) {
      return;
    }

    this.hasCheckedSilentAuth = true;

    // Try silent authentication
    this.authService
      .getAccessTokenSilently({
        authorizationParams: {
          audience: environment.auth.authorizationParams.audience,
          scope: environment.auth.authorizationParams.scope,
        },
      })
      .pipe(tap(() => this._cdr.markForCheck())).subscribe({
        next: (token) => {
          // Silent auth succeeded - Auth0 session exists!
          // Use the shared service to process token
          // Pass false to showLoader since this is silent auth (no loader needed)
          this.tokenHandlerService.processToken(token, false);
        },
        error: () => {
          // Silent auth failed - no Auth0 session cookie
          // This is normal if user hasn't logged in yet
          // Don't auto-redirect, let user click login button
          // Error handling is silent - user can manually log in if needed
        },
      });
  }

  /**
   * This method used to store planId
   */
  setPlanInLocalStorage(): void {
    this.subscriptionService.checkSubscriptionPlan().pipe(tap(() => this._cdr.markForCheck())).subscribe({
      next: (res: SubscriptionPlan) => {
        this.subscriptionService.setSubscriptionData(res);
        if (res.planName === this.constant.free) {
          this.subscriptionService.setPlanId(7);
        }
        if (res.isActive) {
          this.subscriptionService.setPlanId(res.planId);
        } else {
          this.subscriptionService.setPlanId(-1);
        }
      },
      error: () => {
        this.toastrService.error(
          this.constant.errorFetchingSubscriptionDetails,
        );

        this.authenticationService.auth0Logout();
      },
    });
  }
}
